// Handles authentication logic across the app
const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

function getAuthVerifyUrl() {
  return `${API_BASE.replace(/\/api$/i, "")}/verify`;
}

function getStoredToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    ""
  ).trim().replace(/^Bearer\s+/i, "");
}

function getStoredUser() {
  try {
    const rawUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    const user = rawUser ? JSON.parse(rawUser) : null;
    return user?.id ? user : null;
  } catch {
    return null;
  }
}

export function clearStoredAuth() {
  [localStorage, sessionStorage].forEach((storage) => {
    try {
      ["token", "authToken", "jwt", "accessToken", "access_token", "user", "authUser", "currentUser"].forEach(key => storage.removeItem(key));
    } catch {
      // ignore storage failures
    }
  });
}

export async function clearBrowserSessionData() {
  for (const storage of [localStorage, sessionStorage]) {
    try { storage.clear(); } catch { /* Storage may be unavailable. */ }
  }
  if (typeof caches !== "undefined") {
    try { await Promise.all((await caches.keys()).map(name => caches.delete(name))); } catch { /* Continue signing out. */ }
  }
  if (typeof indexedDB !== "undefined" && typeof indexedDB.databases === "function") {
    try {
      await Promise.all((await indexedDB.databases()).filter(database => database.name).map(database => new Promise(resolve => {
        const request = indexedDB.deleteDatabase(database.name);
        request.onsuccess = request.onerror = request.onblocked = resolve;
      })));
    } catch { /* Continue signing out. */ }
  }
}

export const checkUserAuth = () => {
  try {
    const rawUser = localStorage.getItem("user");
    if (!rawUser) return null;

    const parsedUser = JSON.parse(rawUser);
    if (!parsedUser?.id) return null;

    return parsedUser;
  } catch (error) {
    console.error("Error checking auth:", error);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    return null;
  }
};

export const verifyStoredAuthSession = async () => {
  const token = getStoredToken();
  if (!token) return null;

  const storedUser = getStoredUser();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(getAuthVerifyUrl(), {
      signal: controller.signal,
      credentials: "include",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // A verification started before Logout must not restore the old session.
    if (getStoredToken() !== token) return null;

    // Only an explicit authentication failure should end a session. A backend
    // restart, temporary database issue, or network interruption must not
    // erase a valid local session and force the user back to Login.
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) { clearStoredAuth(); return null; }
      return storedUser;
    }

    const payload = await response.json();
    if (getStoredToken() !== token) return null;
    const verifiedUser = payload?.user || null;
    if (!verifiedUser?.id) return storedUser;

    localStorage.setItem("user", JSON.stringify(verifiedUser));
    return verifiedUser;
  } catch (error) {
    if (getStoredToken() !== token) return null;
    console.warn("Unable to verify auth session; keeping the existing session:", error);
    return storedUser;
  } finally {
    clearTimeout(timeout);
  }
};

export const logoutUser = async () => {
  const token = getStoredToken();
  clearStoredAuth();
  const cleanup = clearBrowserSessionData();
  if (token) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(`${API_BASE.replace(/\/api$/i, "")}/logout`, {
        method: "POST", credentials: "include", keepalive: true,
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok && response.status !== 401 && response.status !== 403) {
        console.warn("Browser signed out; server session revocation could not be confirmed.");
      }
    } catch {
      console.warn("Browser signed out; server session revocation could not be confirmed.");
    } finally {
      clearTimeout(timeout);
    }
  }
  await cleanup;
};
